import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentResult() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const sessionId = params.get("session_id");
  const isCancel = location.pathname.includes("/cancel");
  const [status, setStatus] = useState(isCancel ? "cancelled" : "polling");
  const [record, setRecord] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (isCancel || !sessionId) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setRecord(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.payment_status === "expired" || data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch (e) { /* transient */ }
      if (attempts.current >= 20) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, isCancel]);

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6" data-testid="payment-result-page">
      <div className="max-w-md w-full text-center p-10 bg-white border border-[#E5E0D8] rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.04)]">
        {status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-[#2C4C3B] animate-spin" />
            <h1 className="mt-6 font-serif-display text-3xl text-[#1C201E]">Confirming your gift…</h1>
            <p className="mt-2 text-sm text-[#5C6661]">This can take a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-10 h-10 mx-auto text-[#2C4C3B]" />
            <h1 data-testid="payment-success-title" className="mt-6 font-serif-display text-3xl text-[#1C201E]">Thank you for your gift!</h1>
            {record?.amount && <p className="mt-2 text-[#5C6661]">You gave <b>${record.amount}</b>. May the Lord bless you.</p>}
          </>
        )}
        {status === "cancelled" && (
          <>
            <XCircle className="w-10 h-10 mx-auto text-[#5C6661]" />
            <h1 data-testid="payment-cancelled-title" className="mt-6 font-serif-display text-3xl text-[#1C201E]">Giving cancelled</h1>
            <p className="mt-2 text-sm text-[#5C6661]">No card was charged. You can try again anytime.</p>
          </>
        )}
        {(status === "expired" || status === "timeout") && (
          <>
            <XCircle className="w-10 h-10 mx-auto text-red-600" />
            <h1 className="mt-6 font-serif-display text-3xl text-[#1C201E]">We couldn't confirm your gift</h1>
            <p className="mt-2 text-sm text-[#5C6661]">If your card was charged, please contact us and we'll look into it.</p>
          </>
        )}
        <Link data-testid="return-home-link" to="/" className="mt-8 inline-block px-6 py-3 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326]">
          Return home
        </Link>
      </div>
    </div>
  );
}
